import _ from 'lodash';

import PropTypes from 'prop-types';

import React from 'react';
import { Flex, Box } from 'reflexbox';
import {
  Input,
  Button,
  Checkbox,
  Text,
  Divider,
  Message,
} from 'rebass';
import Loader from 'react-loading';
import axios from 'axios';
import * as Sentry from '@sentry/browser';

import mailcheck from 'shared/mailcheck';
import SharedConfig from 'shared/config';

import If from 'frontend/components/If';
import ToggleView from 'frontend/components/ToggleView';
import DarkerBox from 'frontend/components/DarkerBox';
import { PrivacyPolicy } from 'frontend/components/Pages/PrivacyPolicy';
import { TermsOfUse } from 'frontend/components/Pages/TermsOfUse';

import config from 'frontend/config';

class RegisterPanel extends React.Component {
  static propTypes = {
    onViewLoginClick: PropTypes.func.isRequired,

    onSuccess: PropTypes.func.isRequired,
  };

  state = {
    username: '',
    email: '',
    emailConfirm: '',
    password: '',
    emailSuggestion: null,
    acceptPrivacy: false,
    acceptEuProcessing: false,

    loading: false,
    error: null,
  };

  onEmailBlur = async () => {
    this.setState(async prevState => {
      const emailSuggestion = await mailcheck(prevState.email);
      return {
        emailSuggestion,
      };
    });
  };

  onInputEmail = async (event) => {
    const email = _.trim(event.target.value);
    this.setState({ email, emailSuggestion: null, error: null });
  };

  registerOnEnter = (e) => {
    if (e.key === 'Enter') {
      this.register();
    }
  };

  register = async () => {
    if (!this.state.username) {
      this.setState({ error: 'No username entered' });
      return;
    }

    // check email for email-i-ness
    if (!this.state.email) {
      this.setState({ error: 'No email entered' });
      return;
    }

    // check for re-entry error
    if (this.state.email !== this.state.emailConfirm) {
      this.setState({ error: 'Email and Re-enter Email do not match' });
      return;
    }

    // make sure password is long enough
    if (!this.state.password) {
      this.setState({ error: 'No password entered' });
      return;
    }

    if (!this.state.acceptPrivacy) {
      this.setState({ error: 'You must agree to the Raidbots Privacy Policy and  Terms of Use to create an account' });
      return;
    }

    if (!this.state.acceptEuProcessing) {
      this.setState({
        error: 'You must agree to allow Raidbots to process your information outside the EU to create an account',
      });
      return;
    }

    try {
      this.setState({ loading: true, error: null });
      //response = await axios.post('/api/stripe/subscription/resume', null, { disableRetry: true });
      const response = await axios.post('/api/account', {
        username: this.state.username,
        email: this.state.email,
        password: this.state.password,
        touVersion: SharedConfig.legal.touVersion,
      }, {
        disableRetry: true,
      });
      this.setState({ loading: false });
      this.props.onSuccess(response.data);
    } catch (error) {
      let errorMessage = 'Unknown error occurred';
      const status = _.get(error, 'response.status');
      const errorResponse = _.get(error, 'response.data.error');
      if (errorResponse) {
        errorMessage = errorResponse;
      } else if (status === 429) {
        errorMessage = 'Too many failed attempts, try again in 15 minutes';
      } else if (status === 502) {
        errorMessage = 'Temporary server error - please try again';
      } else if (!status) {
        errorMessage = 'A network error occurred - check your connectivity';
      } else {
        Sentry.captureException(new Error(`Unhandled user create error: ${error.message}`));
      }
      this.setState({ loading: false, error: errorMessage });
    }
  };

  render() {
    const { onViewLoginClick } = this.props;
    return (
      <Box>
        <form name="register" onSubmit={e => e.preventDefault()}>
          <Input
            label="Username"
            id="registerUsername"
            name="newUsername"
            type="username"
            autoComplete="username"
            maxLength={20}
            value={this.state.username}
            onChange={e => this.setState({ username: e.target.value, error: null })}
            onKeyPress={this.registerOnEnter}
          />

          <Input
            label="Email"
            id="registerEmail"
            name="newEmail"
            type="email"
            autoComplete="email"
            maxLength={254}
            value={this.state.email}
            onBlur={this.onEmailBlur}
            onChange={this.onInputEmail}
            onKeyPress={this.registerOnEnter}
            mb={0}
          />
          <If
            condition={this.state.emailSuggestion}
            then={() => (
              <Text>
                Did you mean{' '}
                {/* eslint-disable-next-line */}
                <a
                  href="#"
                  onClick={() => this.setState(prevState => ({
                    email: prevState.emailSuggestion.full,
                    emailSuggestion: null,
                  }))}
                >
                  {this.state.emailSuggestion.address}@<strong>{this.state.emailSuggestion.domain}</strong>
                </a>?
              </Text>
            )}
          />

          <Input
            mt={2}
            label="Re-enter Email"
            id="registerEmailConfirm"
            name="emailConfirm"
            type="email"
            autoComplete="email"
            maxLength={254}
            value={this.state.emailConfirm}
            onChange={e => this.setState({ emailConfirm: e.target.value })}
            onKeyPress={this.registerOnEnter}
          />

          <Input
            label="Password"
            id="registerPassword"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            maxLength={255}
            value={this.state.password}
            onChange={e => this.setState({ password: e.target.value, error: null })}
            onKeyPress={this.registerOnEnter}
          />

          <Divider my={2} />

          <ToggleView
            label="Privacy Policy"
            defaultOpen={false}
          >
            <DarkerBox style={{ maxHeight: 400, overflowY: 'scroll' }}>
              <PrivacyPolicy />
            </DarkerBox>
          </ToggleView>

          <Divider my={2} />

          <ToggleView
            label="Terms of Use"
            defaultOpen={false}
          >
            <DarkerBox style={{ maxHeight: 400, overflowY: 'scroll' }}>
              <TermsOfUse />
            </DarkerBox>
          </ToggleView>

          <Divider my={2} />

          <Checkbox
            name="acceptPrivacy"
            id="registerAcceptPrivacy"
            label="I agree to the Raidbots Privacy Policy and Terms of Use"
            checked={this.state.acceptPrivacy}
            onChange={() => this.setState(prevState => ({ acceptPrivacy: !prevState.acceptPrivacy, error: null }))}
            backgroundColor={config.colors.black}
            my={2}
          />

          <Checkbox
            name="acceptEuProcessing"
            id="registerAcceptEuProcessing"
            label="I agree to Raidbots processing my information outside of the EU"
            checked={this.state.acceptEuProcessing}
            onChange={() => this.setState(prevState => ({
              acceptEuProcessing: !prevState.acceptEuProcessing,
              error: null,
            }))}
            backgroundColor={config.colors.black}
            my={2}
          />

          <Flex align="center">
            <Box mr={1}>
              <Button
                id="registerSubmit"
                theme="success"
                onClick={this.register}
                disabled={this.state.loading}
              >
                Create Account
              </Button>
            </Box>
            <Box>
              {this.state.loading
                ? (
                  <Loader
                    type="spin"
                    color={config.colors.green}
                    height={20}
                    width={20}
                    delay={100}
                  />
                ) :
                null}
            </Box>
          </Flex>
          <If
            condition={this.state.error}
            then={() => (
              <Message theme="warning" p={1} mt={2}>
                {this.state.error}
              </Message>
            )}
          />

          <Divider my={1} />

          <Text>
            <a
              href="#login"
              onClick={onViewLoginClick}
            >
              Already have an account?
            </a>
          </Text>
        </form>
      </Box>
    );
  }
}

export default RegisterPanel;
